import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminEpisodes = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFeatured, setFilterFeatured] = useState('all');
  const [dialog, setDialog] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });
  const navigate = useNavigate();

  const showAlert = (title, message, onConfirm = null) => {
    setDialog({ isOpen: true, type: 'alert', title, message, onConfirm });
  };

  const showConfirm = (title, message, onConfirm) => {
    setDialog({ isOpen: true, type: 'confirm', title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    fetchEpisodes();
  }, [currentPage, searchTerm, filterFeatured]);

  const fetchEpisodes = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: '10'
      });

      if (searchTerm) params.append('search', searchTerm);
      if (filterFeatured !== 'all') params.append('is_featured', filterFeatured);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/episodes/?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch episodes');

      const data = await response.json();
      setEpisodes(data.items);
      setTotalPages(data.pages);
    } catch (error) {
      console.error('Error fetching episodes:', error);
    } finally {
      setLoading(false);
    }
  };

  const executeDelete = async (episodeId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/episodes/${episodeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to delete episode');

      fetchEpisodes();
      showAlert('Deleted', 'Episode has been successfully deleted.');
    } catch (error) {
      console.error('Error deleting episode:', error);
      showAlert('Error', 'Failed to delete episode');
    }
  };

  const handleDelete = (episodeId) => {
    showConfirm(
      'Delete Episode',
      'Are you sure you want to completely delete this episode? This action cannot be undone.',
      () => executeDelete(episodeId)
    );
  };

  const handleToggleFeatured = async (episodeId, currentStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const url = currentStatus 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/episodes/${episodeId}/unfeature`
        : `${process.env.REACT_APP_BACKEND_URL}/api/episodes/${episodeId}/feature`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to toggle featured status');

      fetchEpisodes();
    } catch (error) {
      console.error('Error toggling featured status:', error);
      showAlert('Error', 'Failed to toggle featured status');
    }
  };

  const ToggleSwitch = ({ isActive, onToggle, disabled = false }) => (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 focus:ring-offset-2 focus:ring-offset-charcoal ${
        isActive ? 'bg-electric-yellow' : 'bg-slate-dark'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
          isActive ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  const EpisodeCard = ({ episode }) => (
    <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-4 md:p-6 hover:scale-[1.02] transition-transform duration-300">
      <div className="flex flex-col sm:flex-row items-start gap-4">
        <img
          src={episode.thumbnail}
          alt={episode.title}
          className="w-full sm:w-24 h-48 sm:h-24 object-cover rounded-xl"
        />
        <div className="flex-1 text-left w-full">
          <div className="flex items-center gap-2 mb-2 text-left">
            <span className="bg-electric-yellow text-midnight px-3 py-1 rounded-full text-sm font-bold">
              {episode.episode_number || episode.episodeNumber}
            </span>
            {(episode.is_featured || episode.isFeatured) && (
              <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                Featured
              </span>
            )}
          </div>
          <h3 className="font-bold text-warm-white text-xl mb-2 text-left">{episode.title}</h3>
          <p className="text-soft-gray mb-3 text-left">{episode.description.substring(0, 100)}...</p>
          <div className="flex items-center gap-4 text-sm text-soft-gray mb-4 text-left">
            <span>👤 {episode.guest_name || episode.guestName}</span>
            <span>⏱️ {episode.duration}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-slate-dark/30 mt-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <ToggleSwitch
              isActive={episode.is_featured || episode.isFeatured}
              onToggle={() => handleToggleFeatured(episode.id, (episode.is_featured || episode.isFeatured))}
            />
            <span className="text-soft-gray text-sm text-left">Featured</span>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0">
          <button
            onClick={() => navigate(`/admin/episodes/edit/${episode.id}`)}
            className="flex-1 sm:flex-none bg-electric-yellow hover:bg-electric-yellow/90 text-midnight px-4 py-2 rounded-lg transition-colors duration-300 font-bold text-center"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(episode.id)}
            className="flex-1 sm:flex-none bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors duration-300 text-center"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-electric-yellow text-lg">Loading episodes...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="font-clash text-2xl md:text-3xl font-bold text-electric-yellow">Episodes</h2>
        <button
          onClick={() => navigate('/admin/episodes/create')}
          className="w-full sm:w-auto bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-colors duration-300 text-center"
        >
          + Add Episode
        </button>
      </div>

      {/* Filters */}
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search episodes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
            />
          </div>
          <select
            value={filterFeatured}
            onChange={(e) => setFilterFeatured(e.target.value)}
            className="bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
          >
            <option value="all">All Episodes</option>
            <option value="true">Featured Only</option>
            <option value="false">Non-Featured</option>
          </select>
        </div>
      </div>

      {/* Episodes List */}
      <div className="space-y-4">
        {episodes.length === 0 ? (
          <div className="text-center py-12 text-soft-gray">
            No episodes found. Create your first episode!
          </div>
        ) : (
          episodes.map((episode) => (
            <EpisodeCard key={episode.id} episode={episode} />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 pt-6">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="bg-charcoal/40 text-warm-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-electric-yellow/10 transition-colors duration-300"
          >
            Previous
          </button>
          <span className="text-soft-gray">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="bg-charcoal/40 text-warm-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-electric-yellow/10 transition-colors duration-300"
          >
            Next
          </button>
        </div>
      )}

      {/* Custom Dialog Modal */}
      {dialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-midnight/80 backdrop-blur-sm">
          <div className="bg-slate-dark border border-electric-yellow/30 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-electric-yellow mb-2">{dialog.title}</h3>
            <p className="text-soft-gray mb-6">{dialog.message}</p>
            <div className="flex justify-center gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg font-medium text-soft-gray hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                  closeDialog();
                }}
                className={`px-4 py-2 rounded-lg font-bold transition-transform hover:scale-105 ${
                  dialog.type === 'confirm' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' 
                    : 'bg-electric-yellow text-midnight shadow-lg shadow-electric-yellow/20'
                }`}
              >
                {dialog.type === 'confirm' ? 'Delete' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEpisodes;